import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import { useState, useEffect, useMemo, ReactNode, Ref } from 'react';
import { debounce } from 'lodash';
import SearchTextField from '../SearchTextField/SearchTextField';
import Select from '../Select/Select';
import AscIcon from '@/assets/ascending.svg?react';
import DescIcon from '@/assets/descending.svg?react';
import { ga } from '@/lib/analytics';

const ResourcesPageHeader = ({
  title,
  searchText,
  sortBy,
  onSearchTextChange,
  onSortByChange,
  options,
  sortOrder,
  onSortOrderChange,
  searchPlaceholder,
  propositions,
  loading,
  onSearchAccept,
  onSearchCancel,
  searchPropositionsPopperRef,
  titleAction,
  actions,
  defaultSortBy,
  defaultSortOrder = 'desc',
}: {
  title: ReactNode;
  titleAction?: ReactNode;
  // Rendered on the search/sort row, to the right of the sort control.
  actions?: ReactNode;
  searchText: string;
  sortBy: string;
  onSearchTextChange: (s: string) => void;
  onSortByChange: (s: string) => void;
  options: {
    id: number;
    label: ReactNode;
    value: string;
  }[];
  sortOrder?: 'asc' | 'desc';
  onSortOrderChange?: (v: 'asc' | 'desc') => void;
  searchPlaceholder: string;
  propositions?: ReactNode;
  loading?: boolean;
  onSearchAccept?: () => void;
  onSearchCancel?: () => void;
  searchPropositionsPopperRef?: Ref<HTMLDivElement>;
  defaultSortBy?: string;
  defaultSortOrder?: 'asc' | 'desc';
}) => {
  const [internalSearchText, setInternalSearchText] =
    useState(searchText);

  const debouncedSearchChange = useMemo(
    () =>
      debounce((value: string) => {
        onSearchTextChange(value);
      }, 500),
    [onSearchTextChange],
  );

  useEffect(() => {
    setInternalSearchText(searchText);
  }, [searchText]);

  const handleSearchTextChange = (value: string) => {
    setInternalSearchText(value);
    debouncedSearchChange(value);
  };

  const handleSearchClear = () => {
    setInternalSearchText('');
    onSearchTextChange('');
  };

  const resolvedDefaultSortBy = defaultSortBy ?? options[0]?.value;
  const isDefaultSort =
    sortBy === resolvedDefaultSortBy &&
    (sortOrder == null || sortOrder === defaultSortOrder);

  const applySort = (
    nextSortBy: string,
    nextSortOrder: 'asc' | 'desc',
  ) => {
    if (nextSortBy !== sortBy) {
      onSortByChange(nextSortBy);
    }
    if (sortOrder != null && nextSortOrder !== sortOrder) {
      onSortOrderChange?.(nextSortOrder);
    }
    ga.trackSortApply({
      sort_by: nextSortBy,
      sort_order: sortOrder != null ? nextSortOrder : undefined,
    });
  };

  // `null` arrives from the clear (X) button and from re-clicking the
  // already selected option — both mean "back to the default sort".
  // Picking a different option resets the direction too, so every sort
  // starts from the same baseline.
  const handleSortChange = (value: string | number | null) => {
    applySort(
      value == null ? resolvedDefaultSortBy : String(value),
      defaultSortOrder,
    );
  };

  const handleSortOrderChange = (value: 'asc' | 'desc') => {
    applySort(sortBy, value);
  };

  return (
    <>
      <Title>
        <div>{title}</div>
        {titleAction}
      </Title>
      <Actions>
        <StyledSearchTextField
          placeholder={searchPlaceholder}
          value={internalSearchText}
          onChange={handleSearchTextChange}
          loading={loading}
          onEnterUp={onSearchAccept}
          popperRef={searchPropositionsPopperRef}
          minQueryLength={2}
          onCancel={() => {
            handleSearchClear();
            onSearchCancel?.();
          }}
          propositions={
            internalSearchText && propositions ? (
              <SuggestionsContainer>{propositions}</SuggestionsContainer>
            ) : null
          }
          aria-label="Search resources"
        />
        <ActionsRight>
          <SelectWrapper>
            <Label>
              <FormattedMessage id="sortBy" defaultMessage="Sort By" />
            </Label>
            <Select
              options={options}
              onChange={handleSortChange}
              value={sortBy}
              variant="text"
              showClear={!isDefaultSort}
              aria-label="Sort resources"
            />
            {sortOrder != null && onSortOrderChange && (
              <SortOrderSwitch
                sortOrder={sortOrder}
                onChange={handleSortOrderChange}
              />
            )}
          </SelectWrapper>
          {actions}
        </ActionsRight>
      </Actions>
    </>
  );
};

const Title = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  color: theme.palette.black,
  fontSize: '24px',
  lineHeight: '28px',
  fontWeight: 600,
  marginTop: 40,
  marginBottom: 8,
}));

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
}));

const ActionsRight = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const StyledSearchTextField = styled(SearchTextField)({
  maxWidth: 500,
});

const SuggestionsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const SelectWrapper = styled('div')(() => ({
  display: 'flex',
  flexGrow: 0,
  alignItems: 'center',
}));

const Label = styled('div')(({ theme }) => ({
  color: theme.palette.grey400,
  textWrap: 'nowrap',
  marginRight: theme.spacing(2),
  fontSize: '14px',
  lineHeight: '20px',
}));

const SortOrderSwitchRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  borderRadius: theme.radii.sm,
  border: `1px solid ${theme.palette.primary.main}`,
  height: 40,

  '> div:first-of-type': {
    borderRight: `1px solid ${theme.palette.primary.main}`,
  },
}));

const SortOrderButton = styled('div')<{
  isSelected: boolean;
  isLeft: boolean;
}>(({ theme, isSelected, isLeft }) => ({
  width: 48,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  background: isSelected ? theme.palette.red50 : theme.palette.white,
  borderTopLeftRadius: isLeft ? theme.radii.sm : 0,
  borderBottomLeftRadius: isLeft ? theme.radii.sm : 0,
  borderTopRightRadius: isLeft ? 0 : theme.radii.sm,
  borderBottomRightRadius: isLeft ? 0 : theme.radii.sm,
  cursor: 'pointer',
  transition: 'background 0.2s ease-in-out',
  path: {
    stroke: isSelected
      ? theme.palette.primary.main
      : theme.palette.grey600,
  },
}));

const SortOrderSwitch = ({
  sortOrder,
  onChange,
}: {
  sortOrder: 'asc' | 'desc';
  onChange: (v: 'asc' | 'desc') => void;
}) => {
  return (
    <SortOrderSwitchRoot aria-label="Sort order switch">
      <SortOrderButton
        isSelected={sortOrder === 'asc'}
        isLeft
        onClick={() => onChange('asc')}
        aria-label="Ascending order"
      >
        <AscIcon />
      </SortOrderButton>
      <SortOrderButton
        isSelected={sortOrder === 'desc'}
        isLeft={false}
        onClick={() => onChange('desc')}
        aria-label="Descending order"
      >
        <DescIcon />
      </SortOrderButton>
    </SortOrderSwitchRoot>
  );
};

export default ResourcesPageHeader;
